'use client';

import type { Tournament } from '@/lib/types/auction';
import { cn } from '@/lib/utils';

interface TournamentStatusBadgeProps {
  status: Tournament['status'];
  className?: string;
  showDescription?: boolean;
}

const statusConfig: Record<
  Tournament['status'],
  {
    bg: string;
    text: string;
    border: string;
    icon: string;
    description: string;
    availableActions: string[];
  }
> = {
  DRAFT: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-200',
    icon: '📝',
    description:
      'Initial setup phase. Configure tournament settings and add teams.',
    availableActions: [
      'Edit all tournament settings',
      'Configure teams and pools',
      'Open registration when ready',
    ],
  },
  REGISTRATION: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
    icon: '👥',
    description:
      'Teams can register and join pools. Some settings still editable.',
    availableActions: [
      'Review team registrations',
      'Manage player pools',
      'Close registration to proceed',
    ],
  },
  UPCOMING: {
    bg: 'bg-blue-50',
    text: 'text-blue-800',
    border: 'border-blue-200',
    icon: '🗓️',
    description: 'Registration closed. Preparing for auction phase.',
    availableActions: [
      'Review final registrations',
      'Start auction when ready',
      'No new team registrations allowed',
    ],
  },
  ACTIVE: {
    bg: 'bg-green-50',
    text: 'text-green-800',
    border: 'border-green-200',
    icon: '🏆',
    description: 'Live auction in progress. Settings locked.',
    availableActions: [
      'Monitor auction progress',
      'Handle auction disputes',
      'End auction when complete',
    ],
  },
  COMPLETED: {
    bg: 'bg-purple-50',
    text: 'text-purple-800',
    border: 'border-purple-200',
    icon: '✅',
    description: 'Tournament finished. All results finalized.',
    availableActions: [
      'View final results',
      'Generate reports',
      'No modifications allowed',
    ],
  },
};

export function TournamentStatusBadge({
  status,
  className,
}: TournamentStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <div className="flex flex-col justify-center gap-2">
      <span
        className={cn(
          'inline-flex items-center rounded-full border px-2.5 py-1.5 text-xs font-medium shadow-sm',
          config.bg,
          config.text,
          config.border,
          className
        )}
      >
        <span className="mr-1">{config.icon}</span>
        {status}
      </span>
    </div>
  );
}
